import type { Editor } from '@tiptap/core';

/**
 * The link bubble edits plain URL links (the `link` mark), [[wikilinks]]
 * (the `wikiLink` mark) and [guide|...]-style links to our own pages (the
 * `siteLink` mark): same preview, same open/edit/remove. These helpers let
 * the bubble and the EditableLink extension act on whichever is under the
 * cursor without caring which it is.
 */
export type LinkMarkName = 'link' | 'wikiLink' | 'siteLink';

const LINK_MARK_NAMES: LinkMarkName[] = ['link', 'wikiLink', 'siteLink'];

export function activeLinkMark(editor: Editor): LinkMarkName | null {
   return LINK_MARK_NAMES.find(name => editor.isActive(name)) ?? null;
}

export function isLinkMarkActive(editor: Editor): boolean {
   return activeLinkMark(editor) !== null;
}

// The marks record "open in a new window" differently: `link` uses
// target='_blank', the token marks a boolean `newWindow` attr.
export function activeLinkOpensNewWindow(editor: Editor): boolean {
   const mark = activeLinkMark(editor);
   if (mark === 'wikiLink' || mark === 'siteLink') {
      return Boolean(editor.getAttributes(mark).newWindow);
   }
   if (mark === 'link') {
      return editor.getAttributes('link').target === '_blank';
   }
   return false;
}

// EditableLink's newLinksOpenInNewWindow, for the bubble's new-window switch.
// The bubble reads this while rendering, and destroy() nulls extensionManager.
export function newLinksOpenInNewWindow(editor: Editor): boolean {
   if (editor.isDestroyed) {
      return false;
   }
   const link = editor.extensionManager.extensions.find(extension => extension.name === 'link');
   return Boolean(link?.options.newLinksOpenInNewWindow);
}

/**
 * The target to save when re-applying a wikiLink whose address wasn't changed.
 * A bare wikilink (target === null) names its page by its display text, so
 * editing the text would silently retarget it — [[E-Waste]] renamed to
 * "certified recycler" would point at a page named "certified recycler". When
 * the text diverges, pin the target to the original page so it saves as
 * [[E-Waste|certified recycler]] and keeps its destination. An unchanged text,
 * or an already-pinned target, is returned untouched.
 */
export function wikiLinkSaveTarget(
   attrs: { target?: string | null },
   originalText: string,
   newText: string
): string | null {
   if (attrs.target === null && newText !== originalText) {
      return originalText;
   }
   return attrs.target ?? null;
}

/**
 * The attrs to re-apply to a wikilink or site link whose address wasn't
 * changed: its own attrs with the new-window choice, and a bare wikilink's
 * target pinned when its text changes (see wikiLinkSaveTarget). A site link
 * needs no pinning: its id is in attrs, and the serializer compares the text
 * with defaultText.
 */
export function keptLinkAttrs(
   mark: 'wikiLink' | 'siteLink',
   attrs: Record<string, any>,
   originalText: string,
   newText: string,
   newWindow: boolean
): Record<string, any> {
   const target =
      mark === 'wikiLink' ? { target: wikiLinkSaveTarget(attrs, originalText, newText) } : {};
   return { ...attrs, ...target, newWindow };
}
